/**
 * Sub-dialog for picking which tables get a `.data.sql` upsert script emitted
 * alongside their DDL. Opened from the "Choose data tables…" row in the Schema
 * Blueprint Setup dialog.
 *
 * Two-list (shuttle) UX (blueprints ExclusionPickerDialog):
 *   • LEFT  — Available tables (filterable by the search input).
 *   • RIGHT — Tables selected for data blueprint (always shows all selected,
 *             search doesn't apply here).
 * Buttons move single rows or bulk-move-filtered between the two sides.
 *
 * When the user picks a table we fire a `SELECT COUNT(*)` against it (lazily,
 * cached for the lifetime of the dialog) so we can surface a warning when the
 * row count is high — Database Blueprint is not meant to substitute for a real
 * data dump. Warnings are coloured orange at 200+ rows and red at 2 000+.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { countTableRows } from '../services/databaseBlueprintApi';
import type { UserFacingEngineId } from '../types/databaseBlueprintTypes';
import { encodeTableKey } from '../utils/tableKey';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';

export const DATA_MIRROR_WARN_ORANGE = 200;
export const DATA_MIRROR_WARN_RED = 2000;

type TableRow = { schema: string; name: string };

type CountState =
  | { state: 'unknown' }
  | { state: 'loading' }
  | { state: 'done'; rows: number | null; error?: string; hasPk?: boolean };

type Props = {
  open: boolean;
  onClose: () => void;
  connectionId: string;
  database: string;
  engine: UserFacingEngineId;
  /** Blueprint candidate tables (already filtered by exclusions in the parent). */
  candidates: ReadonlyArray<TableRow>;
  /** How the parent built `candidates`. Drives the empty-state copy: a
   *  `live-db` source prompts the user to connect; a `blueprint-folder` source
   *  tells them the on-disk Tables/ folder had nothing to parse — pointing at
   *  the real cause instead of misattributing it to a missing DB connection.
   *  Optional for back-compat; defaults to `live-db`. */
  candidatesSource?: 'blueprint-folder' | 'live-db';
  /** If the parent's candidate-loader failed (parse error / IPC error),
   *  pass the message so the picker can surface it instead of pretending the
   *  folder/DB is simply empty. */
  candidatesError?: string | null;
  selectedKeys: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  /**
   * Keys (`${schema}.${name}`) of selected rows for which the user has
   * explicitly opted into destructive DELETE+INSERT replay. Only meaningful
   * for tables without a primary key — for PK-bearing tables the emitter
   * always emits a non-destructive upsert and this flag is ignored.
   */
  destructiveKeys?: ReadonlySet<string>;
  /** Pair with `destructiveKeys` — receives the next set on toggle. */
  onDestructiveChange?: (next: Set<string>) => void;
  /**
   * Fired when the user clicks the "Add primary key…" affordance on a no-PK
   * row. Wiring to the Table Designer is owned by the parent (T26); the
   * picker just surfaces the action.
   */
  onAddPrimaryKey?: (row: TableRow) => void;
};

const rowKey = (t: TableRow) => encodeTableKey({ schema: t.schema, name: t.name });

const EMPTY_KEY_SET: ReadonlySet<string> = new Set();

export function BlueprintDataPickerDialog({
  open,
  onClose,
  connectionId,
  database,
  engine,
  candidates,
  candidatesSource = 'live-db',
  candidatesError,
  selectedKeys,
  onChange,
  destructiveKeys,
  onDestructiveChange,
  onAddPrimaryKey,
}: Props) {
  const effectiveDestructive = destructiveKeys ?? EMPTY_KEY_SET;
  const [search, setSearch] = useState('');
  const [counts, setCounts] = useState<Record<string, CountState>>({});
  // Live ref so the async fetch callback always reads the latest state map
  // without forcing the effect to re-run every time a count lands.
  const countsRef = useRef(counts);
  countsRef.current = counts;

  // Per-pane selection model — clicking a row toggles its selection in its
  // own pane. Shuttle buttons act on those sets.
  const [leftSelected, setLeftSelected] = useState<Set<string>>(new Set());
  const [rightSelected, setRightSelected] = useState<Set<string>>(new Set());

  // Reset counts (and pane selections) only when the picker opens. We used to
  // also reset on `candidates` change, but parents typically build the
  // candidate array inline (`.filter().map()`), which means a new array
  // reference on every render — and that wiped already-fetched counts every
  // time the user added another table. Resetting on open alone is sufficient:
  // the picker is modal, so the candidate list can't meaningfully change
  // while it's visible (the Settings dialog underneath is non-interactive),
  // and counts for tables that disappear from candidates simply become
  // unreachable in the UI.
  useEffect(() => {
    if (open) {
      setCounts({});
      setLeftSelected(new Set());
      setRightSelected(new Set());
    }
  }, [open]);

  const fetchCountsFor = useCallback(
    async (rows: ReadonlyArray<TableRow>) => {
      const todo = rows.filter((r) => {
        const k = rowKey(r);
        const s = countsRef.current[k];
        return !s || s.state === 'unknown';
      });
      if (todo.length === 0) return;
      setCounts((prev) => {
        const next = { ...prev };
        for (const r of todo) next[rowKey(r)] = { state: 'loading' };
        return next;
      });
      const result = await countTableRows({
        connectionId,
        database,
        engine,
        tables: todo,
      });
      if (!result.success) {
        setCounts((prev) => {
          const next = { ...prev };
          for (const r of todo) {
            next[rowKey(r)] = { state: 'done', rows: null, error: result.error };
          }
          return next;
        });
        return;
      }
      setCounts((prev) => {
        const next = { ...prev };
        for (const c of result.counts) {
          next[rowKey(c)] = {
            state: 'done',
            rows: c.rows,
            hasPk: c.hasPk,
            ...(c.error ? { error: c.error } : {}),
          };
        }
        return next;
      });
      // Prune destructive flags that were persisted against tables which now
      // turn out to have a primary key. The flag is a no-op on PK-bearing
      // tables (the emitter only honours it when pkColumns is empty), but
      // re-serialising it would round-trip stale state.
      if (destructiveKeys && destructiveKeys.size > 0 && onDestructiveChange) {
        const toClear: string[] = [];
        for (const c of result.counts) {
          if (c.hasPk === true && destructiveKeys.has(rowKey(c))) {
            toClear.push(rowKey(c));
          }
        }
        if (toClear.length > 0) {
          const nextSet = new Set(destructiveKeys);
          for (const k of toClear) nextSet.delete(k);
          onDestructiveChange(nextSet);
        }
      }
    },
    [connectionId, database, engine, destructiveKeys, onDestructiveChange],
  );

  // Eagerly fetch counts for the currently-selected tables on first open so
  // the user sees the warnings without having to re-tick them.
  useEffect(() => {
    if (!open) return;
    const selectedRows = candidates.filter((c) => selectedKeys.has(rowKey(c)));
    if (selectedRows.length === 0) return;
    void fetchCountsFor(selectedRows);
    // We intentionally don't depend on `selectedKeys` here — re-running this
    // every tick would cancel the user's in-progress selection feedback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, candidates, fetchCountsFor]);

  const q = search.trim().toLowerCase();
  const matchesQuery = useCallback(
    (r: TableRow): boolean => {
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        r.schema.toLowerCase().includes(q)
      );
    },
    [q],
  );

  // LEFT pane — non-selected candidates, with search applied.
  const availableRows = useMemo(
    () =>
      candidates.filter(
        (r) => !selectedKeys.has(rowKey(r)) && matchesQuery(r),
      ),
    [candidates, selectedKeys, matchesQuery],
  );

  // RIGHT pane — every currently-selected candidate, ignoring search. Blueprints
  // the ExclusionPickerDialog rule so users can always see what they've
  // committed to even after filtering Available.
  const selectedRows = useMemo(
    () => candidates.filter((r) => selectedKeys.has(rowKey(r))),
    [candidates, selectedKeys],
  );

  // Total count of non-selected (regardless of filter) for the title suffix.
  const totalAvailable = candidates.length - selectedRows.length;

  const moveToSelected = (keys: ReadonlyArray<string>) => {
    if (keys.length === 0) return;
    const next = new Set(selectedKeys);
    for (const k of keys) next.add(k);
    onChange(next);
    setLeftSelected(new Set());
    // Kick off counts for the just-moved rows.
    const justMoved = candidates.filter((c) => keys.includes(rowKey(c)));
    if (justMoved.length > 0) void fetchCountsFor(justMoved);
  };

  const moveToAvailable = (keys: ReadonlyArray<string>) => {
    if (keys.length === 0) return;
    const next = new Set(selectedKeys);
    for (const k of keys) next.delete(k);
    onChange(next);
    setRightSelected(new Set());
    // Prune destructive flags for rows the user just moved back to Available
    // so stale opt-ins don't linger if the row is later re-selected.
    if (onDestructiveChange) {
      let touched = false;
      const pruned = new Set(effectiveDestructive);
      for (const k of keys) {
        if (pruned.delete(k)) touched = true;
      }
      if (touched) onDestructiveChange(pruned);
    }
  };

  const handleMoveRight = () => moveToSelected(Array.from(leftSelected));
  const handleMoveAllRight = () =>
    moveToSelected(availableRows.map(rowKey));
  const handleMoveLeft = () => moveToAvailable(Array.from(rightSelected));
  const handleClearAll = () => moveToAvailable(selectedRows.map(rowKey));

  const toggleLeftSelection = (r: TableRow) => {
    const k = rowKey(r);
    setLeftSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
    // Eagerly count rows for any table the user clicks in the Available pane
    // so they see the row count (and any large-table warning) before deciding
    // to move it right. Idempotent — `fetchCountsFor` skips tables whose
    // count is already loading or done.
    void fetchCountsFor([r]);
  };

  const toggleRightSelection = (r: TableRow) => {
    const k = rowKey(r);
    setRightSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const toggleDestructive = useCallback(
    (r: TableRow) => {
      if (!onDestructiveChange) return;
      const k = rowKey(r);
      const next = new Set(effectiveDestructive);
      const enabling = !next.has(k);
      if (enabling) next.add(k);
      else next.delete(k);
      onDestructiveChange(next);
      window.electronAPI?.analytics
        ?.track('feature:database_blueprint_data_blueprint_destructive_toggled', {
          engine,
          enabled: enabling,
        })
        ?.catch(() => {
          /* fire-and-forget */
        });
    },
    [effectiveDestructive, onDestructiveChange, engine],
  );

  // Per-row extras renderer for the Selected pane: only no-PK rows get the
  // amber warning chip + Allow-destructive checkbox + Add-PK button. PK-bearing
  // and still-loading rows render nothing extra.
  const renderSelectedRowExtras = useCallback(
    (row: TableRow): ReactNode => {
      const key = rowKey(row);
      const c = counts[key];
      if (!c || c.state !== 'done' || c.hasPk !== false) return null;
      return (
        <NoPkRowWarning
          row={row}
          allowDestructive={effectiveDestructive.has(key)}
          onToggleDestructive={() => toggleDestructive(row)}
          onAddPrimaryKey={
            onAddPrimaryKey ? () => onAddPrimaryKey(row) : undefined
          }
        />
      );
    },
    [counts, effectiveDestructive, onAddPrimaryKey, toggleDestructive],
  );

  // Worst row count across selected tables — drives the dialog-level banner.
  const worstSelectedCount = useMemo(() => {
    let worst = -1;
    for (const k of selectedKeys) {
      const s = counts[k];
      if (s?.state === 'done' && s.rows !== null && s.rows > worst) {
        worst = s.rows;
      }
    }
    return worst;
  }, [selectedKeys, counts]);
  const showRedBanner = worstSelectedCount >= DATA_MIRROR_WARN_RED;
  const showOrangeBanner =
    !showRedBanner && worstSelectedCount >= DATA_MIRROR_WARN_ORANGE;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-4xl w-[min(64rem,calc(100vw-2rem))]"
        data-testid="database-blueprint-blueprint-data-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Choose data tables</DialogTitle>
          <DialogDescription>
            Pick tables whose <strong>data</strong> should also be exported as a
            <code className="mx-1 text-[11px]">[schema].[name].data.sql</code>
            upsert script next to the table&rsquo;s DDL. Designed for small
            lookup tables — large tables get a warning.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {(showOrangeBanner || showRedBanner) && (
            <div
              className={cn(
                'flex items-start gap-2 rounded-sm border px-2 py-1.5 text-xs',
                showRedBanner
                  ? 'border-destructive/40 bg-destructive/10 text-destructive'
                  : 'border-amber-500/40 bg-amber-50/50 text-amber-900 dark:bg-amber-900/15 dark:text-amber-200',
              )}
              data-testid="database-blueprint-blueprint-data-warning"
            >
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                {showRedBanner
                  ? `One or more selected tables has ${DATA_MIRROR_WARN_RED.toLocaleString()}+ rows — Database Blueprint sync will be slow. Consider a proper data dump for large tables.`
                  : `One or more selected tables has ${DATA_MIRROR_WARN_ORANGE.toLocaleString()}+ rows — sync may take longer than usual.`}
              </span>
            </div>
          )}

          {/* Search bar — applies to the LEFT (Available) pane only. */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by name or schema"
                className="pl-8 h-8 text-sm"
                data-testid="database-blueprint-blueprint-data-search"
              />
            </div>
          </div>

          {/* Two-list shuttle */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-stretch">
            <ListPane
              title="Available"
              testId="database-blueprint-blueprint-data-available"
              rows={availableRows}
              selected={leftSelected}
              onToggle={toggleLeftSelection}
              counts={counts}
              emptyText={
                candidates.length === 0
                  ? candidatesError
                    ? `Couldn't list tables: ${candidatesError}`
                    : candidatesSource === 'blueprint-folder'
                      ? 'No tables found in the blueprint folder. Run "Refresh from DB" to repopulate Tables/*.sql.'
                      : 'No tables available — connect to a source DB first.'
                  : totalAvailable === 0
                    ? 'Everything is selected for data blueprint.'
                    : 'No matches for the current filter.'
              }
              countSuffix={`/ ${totalAvailable}`}
            />

            <div className="flex flex-col items-center justify-center gap-1.5 px-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleMoveRight}
                disabled={leftSelected.size === 0}
                title="Select for data blueprint"
                data-testid="database-blueprint-blueprint-data-move-right"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleMoveAllRight}
                disabled={availableRows.length === 0}
                title="Select all filtered"
                data-testid="database-blueprint-blueprint-data-move-all-right"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
              <div className="h-1" />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleMoveLeft}
                disabled={rightSelected.size === 0}
                title="Remove from data blueprint"
                data-testid="database-blueprint-blueprint-data-move-left"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleClearAll}
                disabled={selectedRows.length === 0}
                title="Clear all"
                data-testid="database-blueprint-blueprint-data-clear"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
            </div>

            <ListPane
              title="Selected for data blueprint"
              testId="database-blueprint-blueprint-data-selected"
              rows={selectedRows}
              selected={rightSelected}
              onToggle={toggleRightSelection}
              counts={counts}
              emptyText="No tables selected — nothing will be blueprint-dataed."
              countSuffix={`/ ${selectedRows.length}`}
              renderRowExtras={renderSelectedRowExtras}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            onClick={onClose}
            data-testid="database-blueprint-blueprint-data-done"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ListPane({
  title,
  rows,
  selected,
  onToggle,
  counts,
  emptyText,
  countSuffix,
  testId,
  renderRowExtras,
}: {
  title: string;
  rows: ReadonlyArray<TableRow>;
  selected: ReadonlySet<string>;
  onToggle: (row: TableRow) => void;
  counts: Record<string, CountState>;
  emptyText: string;
  countSuffix?: string;
  testId: string;
  /**
   * Optional secondary content rendered beneath each row's primary line.
   * Used by the Selected pane to surface the no-PK warning + Allow-destructive
   * checkbox + Add-PK button. Available pane omits this so users only see
   * destructive opt-ins for tables they've already committed to.
   */
  renderRowExtras?: (row: TableRow) => ReactNode;
}) {
  return (
    <div
      className="flex flex-col min-h-0 border rounded-sm overflow-hidden"
      data-testid={testId}
    >
      <div className="flex items-center justify-between px-2 py-1 border-b bg-muted/30 text-xs">
        <span className="font-semibold uppercase tracking-wide">{title}</span>
        <span className="text-[10px] text-muted-foreground">
          {rows.length}
          {countSuffix ? ` ${countSuffix}` : ''}
        </span>
      </div>
      <ScrollArea className="h-[320px]">
        {rows.length === 0 ? (
          <div className="text-xs text-muted-foreground py-3 px-2">{emptyText}</div>
        ) : (
          <ul className="space-y-0.5 p-1">
            {rows.map((row) => {
              const key = rowKey(row);
              const isSelected = selected.has(key);
              const c = counts[key];
              const extras = renderRowExtras?.(row);
              return (
                <li
                  key={key}
                  onClick={() => onToggle(row)}
                  className={cn(
                    'flex flex-col gap-1 px-2 py-1 rounded-sm cursor-pointer min-w-0',
                    isSelected
                      ? 'bg-accent text-accent-foreground'
                      : 'hover:bg-accent/50',
                  )}
                  data-testid={`database-blueprint-blueprint-data-row-${row.schema}-${row.name}`}
                  data-selected={isSelected ? 'true' : undefined}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Checkbox
                      checked={isSelected}
                      className="h-3.5 w-3.5 shrink-0 pointer-events-none"
                      tabIndex={-1}
                      aria-hidden
                    />
                    <span
                      className="text-xs font-medium truncate min-w-0 flex-1"
                      title={row.schema ? `${row.schema}.${row.name}` : row.name}
                    >
                      {row.schema ? `${row.schema}.` : ''}
                      {row.name}
                    </span>
                    <RowCountBadge state={c} />
                  </div>
                  {extras}
                </li>
              );
            })}
          </ul>
        )}
      </ScrollArea>
    </div>
  );
}

/**
 * Inline warning block rendered beneath no-PK rows in the Selected pane.
 * Surfaces the missing-PK state, lets users opt into destructive DELETE+INSERT
 * replay, and offers an Add-PK shortcut (wiring to Table Designer is owned by
 * the parent — T26).
 *
 * All controls stop click propagation so they don't accidentally toggle the
 * parent row's pane-selection state.
 */
function NoPkRowWarning({
  row,
  allowDestructive,
  onToggleDestructive,
  onAddPrimaryKey,
}: {
  row: TableRow;
  allowDestructive: boolean;
  onToggleDestructive: () => void;
  onAddPrimaryKey?: () => void;
}) {
  const checkboxId = `database-blueprint-blueprint-data-allow-destructive-${row.schema}-${row.name}-cb`;
  return (
    <div className="flex flex-wrap items-center gap-2 pl-1 pt-1">
      <span
        className="inline-flex items-center gap-1 rounded-sm border border-amber-500/40 bg-amber-50/60 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-900/15 dark:text-amber-200"
        data-testid={`database-blueprint-blueprint-data-nopk-warning-${row.schema}-${row.name}`}
      >
        <AlertTriangle className="h-3 w-3" />
        No primary key
      </span>
      <label
        htmlFor={checkboxId}
        className="inline-flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer"
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          id={checkboxId}
          checked={allowDestructive}
          onCheckedChange={() => onToggleDestructive()}
          onClick={(e) => e.stopPropagation()}
          className="h-3 w-3"
          data-testid={`database-blueprint-blueprint-data-allow-destructive-${row.schema}-${row.name}`}
        />
        Allow destructive replay
      </label>
      {onAddPrimaryKey && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-5 px-1.5 text-[10px]"
          onClick={(e) => {
            e.stopPropagation();
            onAddPrimaryKey();
          }}
          data-testid={`database-blueprint-blueprint-data-add-pk-${row.schema}-${row.name}`}
        >
          Add primary key…
        </Button>
      )}
    </div>
  );
}

function RowCountBadge({ state }: { state: CountState | undefined }) {
  if (!state || state.state === 'unknown') {
    return (
      <span className="text-[10px] text-muted-foreground shrink-0">—</span>
    );
  }
  if (state.state === 'loading') {
    return (
      <span className="shrink-0">
        <RandomDotmSpinner size={10} />
      </span>
    );
  }
  if (state.rows === null) {
    return (
      <span
        className="text-[10px] text-muted-foreground italic shrink-0"
        title={state.error}
      >
        n/a
      </span>
    );
  }
  const tone =
    state.rows >= DATA_MIRROR_WARN_RED
      ? 'text-destructive'
      : state.rows >= DATA_MIRROR_WARN_ORANGE
        ? 'text-warning'
        : 'text-muted-foreground';
  return (
    <span
      className={cn('text-[10px] font-mono shrink-0', tone)}
      data-testid={`row-count-${state.rows >= DATA_MIRROR_WARN_RED ? 'red' : state.rows >= DATA_MIRROR_WARN_ORANGE ? 'orange' : 'normal'}`}
    >
      {state.rows.toLocaleString()} rows
    </span>
  );
}
